// ── Design decisions ──
// Modal centrado reservado para acciones terminales (cerrar manifiesto,
// reabrir). Por eso no tiene "clic afuera cierra": la decisión debe ser
// explícita con un botón o Esc. El foco entra al diálogo y se restaura al
// salir. `actions` se renderiza tal cual en el pie para que quien lo use
// controle el orden y el estado disabled de sus botones.

import { useEffect, useRef } from 'react';
import { X } from 'lucide-react';

const Modal = ({ open, title, children, onClose, actions = null }) => {
  const boxRef = useRef(null);
  const previoRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    previoRef.current = document.activeElement;
    boxRef.current?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose && onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      const prev = previoRef.current;
      if (prev && typeof prev.focus === 'function') prev.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div
        ref={boxRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className="w-full max-w-lg rounded-lg bg-white shadow-xl outline-none"
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar diálogo"
            title="Cerrar diálogo (Esc)"
            className="rounded-md p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          >
            <X size={16} aria-hidden="true" />
          </button>
        </div>
        <div className="px-4 py-3 text-sm text-slate-700">{children}</div>
        {actions ? (
          <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-4 py-3">
            {actions}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default Modal;
